import classNames from 'classnames';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_CLASS_LOCAL } from '../../../store/constants';
import { MATERIAL_ICON_SETTINGS } from '../../../shared/common/Button/Icon';
import ProductAge from '../../../shared/product/ProductAge';
import ProductAuthors from '../../../shared/product/ProductAuthors';
import ProductGenres from '../../../shared/product/ProductGenres';
import { ProductReleaseDate } from '../../../shared/product/ProductReleaseDate';
import ProductSeries from '../../../shared/product/ProductSeries';
import ProductLanguage from '../../../shared/product/ProductLanguage';
import { PreviewButton } from '../../../shared/preview/PreviewButton';
import Utils from '../../../shared/utils';
import RoutingUtils from '../../../shared/utils/utils-routing';
import LibxAddToCollectionButton from '../../libx/shared/LibxAddToCollectionButton';
import { LibxStatisticsWithStockDetails } from '../../libx/shared/LibxStatisticsWithStockDetails';
import LibxTitleInfoLine from '../shared/LibxTitleInfoLine';
import { LOANFORMAT_EBOOK_OBJECT } from '../../../store/loanFormat/actions';
import ProductStatusLine from './ProductStatusLine';
import { ProductCover } from './ProductCover';
import { DISPLAY_TYPE_LIST, DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import LibraryManagedProductGroupDropdown from '../../../shared/common/LibraryManagedProductGroupDropdown';
import { ProductOut } from '../../../store/api';
import { LibraryManagedProductGroupOnSiteOut } from '../../../store/api-catman';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

interface Props {
    hoverListener?: (isHover: boolean) => void;
    id?: string;
    product?: any;
    productLinkUri: string;
    publisher?: string;
    imprint?: string;
    isYourTitle: boolean;
    isInCollection: boolean;
    isLibxchange: boolean;
    displayType?: typeof DISPLAY_TYPE_MINIMAL | typeof DISPLAY_TYPE_TILE | typeof DISPLAY_TYPE_LIST;
    onAddToCollection?: (product: ProductOut) => void;
    updateProduct?: (product: ProductOut) => void;
    onManageButtonClick?: (product: ProductOut) => void;
    animationTime?: number;
    productGroups?: LibraryManagedProductGroupOnSiteOut[];
    onAddToGroup?: (productGroupId: number, productId: string) => void;
}

const ProductTile = (props: Props) => {
    const [showDetails, setShowDetails] = useState(false);
    const { t } = useTranslation();

    function handleShowDetails() {
        setShowDetails(true);
        if (props.hoverListener) {
            props.hoverListener(true);
        }
    }

    function hideDetails() {
        setShowDetails(false);

        if (props.hoverListener) {
            props.hoverListener(false);
        }
    }

    const productIsLibx = props.isLibxchange;
    const productIsYourTitle = props.isYourTitle;
    const productIsLCCollection = props.isInCollection;

    const isMinimalView = props.displayType === DISPLAY_TYPE_MINIMAL;
    const isShowDetails = showDetails;
    const isCoverDetailsToShow = !isMinimalView && !isShowDetails;

    const loanFormatKey = LOANFORMAT_EBOOK_OBJECT.key;

    const product = {
        ...props.product,
        loanFormat: loanFormatKey
    };

    if (isEqual(product.ageGroup, 'All')) {
        delete product.ageGroup;
    }

    const publisherName = !productIsYourTitle ? product.publisher : props.publisher;
    const shortenedTitle = Utils.truncate(product.title);

    const tileClasses = classNames('prd-tile-inner', {
        over: showDetails
    });

    //TODO: Remove feature toggle and add the Product Language Component for real
    //TODO: Remove this in a separate MR

    const productTileClassName = classNames('prd-tile', { minimal: isMinimalView });

    return (
        <div className={productTileClassName} onMouseLeave={hideDetails}>
            <div className={tileClasses}>
                <div className="packshot" data-displayname="ProductTile">
                    <Link
                        to={props.productLinkUri}
                        onClick={e => {
                            e.preventDefault();
                            RoutingUtils.pushUrl(props.productLinkUri);
                        }}
                    >
                        <ProductCover
                            onMouseEnter={handleShowDetails}
                            title={product.title}
                            productId={product.id}
                            coverUrl={product.coverUrl}
                            minSizeInPx={300}
                        />
                    </Link>
                    {productIsYourTitle ? null : <PreviewButton product={product} />}
                </div>
                <div className="details" data-displayname="ProductTileDetails">
                    <ProductSeries product={product} linked={false} loanFormatKey={loanFormatKey} />
                    <h3 onMouseEnter={handleShowDetails}>
                        <Link to={props.productLinkUri}>{shortenedTitle}</Link>
                    </h3>
                    <ProductAuthors
                        onMouseEnter={handleShowDetails}
                        product={product}
                        linked={false}
                        loanFormatKey={loanFormatKey}
                    />
                    {!productIsYourTitle ? null : <ProductStatusLine status={product.activeStatus} />}
                    {!isShowDetails ? null : (
                        <div className="prd-bottom">
                            <ProductGenres linked={false} product={product} loanFormatKey={loanFormatKey} />
                            <ProductAge linked={false} product={product} loanFormatKey={loanFormatKey} />

                            <ProductLanguage
                                product={{ ...product, productClass: PRODUCT_CLASS_LOCAL }}
                                loanFormatKey={loanFormatKey}
                                withoutLink={true}
                                label={product.language}
                            />

                            <ProductReleaseDate product={product} />
                            <p>
                                {publisherName} - {product.publisherImprint}
                            </p>
                            <p>
                                {t('components.productFormat.eBook.none', {
                                    pages: product.pages
                                })}
                            </p>
                            <p>{t('components.productUsageConditions.simultaneousAccess')}</p>
                            {!productIsLCCollection ? null : (
                                <LibxTitleInfoLine isLibxTitle={product.fromLibXchange || false} />
                            )}
                            {productIsYourTitle ? null : (
                                <React.Fragment>
                                    <div style={{ height: '5px' }} />
                                    {!productIsLCCollection ? null : (
                                        <div className="manage-button-wrapper">
                                            <Button
                                                text={t('manage')}
                                                fullWidth={true}
                                                icon={MATERIAL_ICON_SETTINGS}
                                                onClick={() =>
                                                    props.onManageButtonClick
                                                        ? props.onManageButtonClick(props.product ?? {})
                                                        : null
                                                }
                                                size={ButtonSize.SMALL}
                                                theme={ButtonTheme.NEUTRAL}
                                            />
                                            <LibraryManagedProductGroupDropdown
                                                productGroups={props.productGroups}
                                                onListClick={productGroupId =>
                                                    props.onAddToGroup
                                                        ? props.onAddToGroup(productGroupId, product.id)
                                                        : null
                                                }
                                            />
                                        </div>
                                    )}
                                    {productIsLibx ? (
                                        <LibxStatisticsWithStockDetails
                                            activeLibraries={product.activeLibraries}
                                            copyAmount={product.copyAmount}
                                            stockDetails={product.stockDetails}
                                        />
                                    ) : null}
                                    {!productIsLibx ? null : (
                                        <div className="libx-add-to-collection-button">
                                            <LibxAddToCollectionButton
                                                updateProduct={props.updateProduct}
                                                product={props.product}
                                                onAddToCollection={props.onAddToCollection}
                                                productGroupId={props.id}
                                                fullWidth={true}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </div>
                {!isCoverDetailsToShow ? null : (
                    <>
                        {!productIsLCCollection ? null : (
                            <div className="manage-button-wrapper">
                                <Button
                                    text={t('manage')}
                                    fullWidth={true}
                                    icon={MATERIAL_ICON_SETTINGS}
                                    onClick={() =>
                                        props.onManageButtonClick
                                            ? props.onManageButtonClick(props.product ?? {})
                                            : null
                                    }
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.NEUTRAL}
                                />
                                <LibraryManagedProductGroupDropdown
                                    productGroups={props.productGroups}
                                    onListClick={productGroupId =>
                                        props.onAddToGroup ? props.onAddToGroup(productGroupId, product.id) : null
                                    }
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ProductTile;
